import Styled from './AirlineFilterToggle.styled';
import AirlineLogo from '@/app-mweb/components/AirlineLogo/AirlineLogo';
import { getLogoImg } from '@/utils/content.utils';
import { Toggle, Typography, TYPOGRAPHY_VARIANT } from '@tajawal/ct-web-design-system';
import { useTranslation } from 'react-i18next';
import useAirlineFilter from '@/hooks/useAirlineFilter';
import { useAB } from '@tajawal/web-common/lib';
import { AB_TESTING_FLAGS } from '@/constants/analytics.constants';
import { useEffect } from 'react';
import { useStore } from '@/contexts/StoreContext';
import { useService } from '@/contexts/ServiceContext';
import _find from 'lodash/find';
import _size from 'lodash/size';
import useStateOfAirlineToggle from './hooks/useStateOfAirlineToggle';
import { pushSameAirlineFilterEvent } from '@/utils/avoGtm.utils';
import { LANGUAGE } from '@tajawal/web-core-sdk-v2/lib/constants';
import { useComputeSplitQueryAttrs } from '@/hooks/useComputeSplitQueryAttrs';

function AirlineFilterToggle({ isDesktop = false }: { isDesktop?: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const { resources: $resources } = useService();
  const { airlineFilter, airlineCode, isResourcesFetched, isReady } = useStateOfAirlineToggle(store);
  const { toggle, isSelected, reset, state, params } = useAirlineFilter(airlineFilter);
  const carrierLogo = getLogoImg(airlineCode);
  const isSelectedAirline = isSelected(airlineCode) && _size(params) === 1;
  const splitAttributes = useComputeSplitQueryAttrs();
  const { active: isEnabledMultiPNRFilter } = useAB(AB_TESTING_FLAGS.MULTI_PNR_FILTER, splitAttributes);
  const isFilterAvailable = _find(state, (item) => item.code === airlineCode);
  const airlineName = $resources.getAirlineLabel(airlineCode);

  useEffect(() => {
    if (isReady && isEnabledMultiPNRFilter && isResourcesFetched) {
      toggle([airlineCode], true);
      pushEvent(true);
    }
  }, [isReady, isEnabledMultiPNRFilter, isResourcesFetched, airlineCode]);

  const handleToggle = () => {
    pushEvent(!isSelectedAirline);
    reset(true);
    if (!isSelectedAirline) {
      toggle([airlineCode], true);
    }
  };

  const pushEvent = (isEnabled: boolean) => {
    pushSameAirlineFilterEvent({
      isEnabled,
      airlineNameEn: $resources.getAirlineLabel(airlineCode, LANGUAGE.EN),
      airlineCode,
    });
  };

  if (!isFilterAvailable) {
    return null;
  }

  return (
    <Styled.Wrapper $isDesktop={isDesktop} onClick={handleToggle}>
      <Styled.FlexWrapper>
        <AirlineLogo logos={[carrierLogo]} />
        <Typography variant={TYPOGRAPHY_VARIANT.CAPTIONSEMBLD} component="p">
          {t('SearchResults.StepByStep_AirlineFilter', {
            airline: airlineName,
          })}
        </Typography>
      </Styled.FlexWrapper>
      <Toggle isChecked={isSelectedAirline} label={airlineName} size="medium" />
    </Styled.Wrapper>
  );
}

export default AirlineFilterToggle;
